'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { locales, localeNames, localeFlags, type Locale, getLocalePath } from '@/lib/i18n';
import { ROUTES } from '@/lib/routes';
import styles from './LanguageSwitcher.module.css';

/**
 * Language Switcher Component
 * Allows users to change locale while preserving current path
 * Opens on hover like navigation menu items
 */
export function LanguageSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const params = useParams();
  const pathname = usePathname();
  const router = useRouter();
  
  // Determine current locale: from params, or detect from pathname
  let currentLocale: Locale = 'en';
  if (params?.locale && locales.includes(params.locale as any)) {
    currentLocale = params.locale as Locale;
  } else if (pathname) {
    const match = locales.find((l) => l !== 'en' && (pathname === `/${l}` || pathname.startsWith(`/${l}/`)));
    if (match) currentLocale = match as Locale;
  }

  // Use emoji flags from i18n (auto-updated when new locales are added).
  const emojiFlag = (l: Locale) => (localeFlags as any)?.[l] || '🌐';

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Helper functions for hover behavior
  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    clearCloseTimeout();
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200); // 200ms delay before closing (same as navigation menu)
  };

  // Toggle function for mobile/touch devices - allows opening and closing with clicks
  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  // Get path without locale prefix (handle any non-default locale, not /en/ since it's at root)
  const rawPath = pathname || '/';
  const nonDefault = locales.filter((l) => l !== 'en');
  const localePrefixRegex = nonDefault.length ? new RegExp(`^/(${nonDefault.join('|')})(?=/|$)`) : /^$/;
  const stripped = rawPath.replace(localePrefixRegex, '');
  const pathWithoutLocale = stripped === '' ? '/' : stripped;

  // Map locale-specific URLs using centralized ROUTES
  const getLocaleSpecificPath = (targetLocale: Locale, currentPath: string): string => {
    // Get full pathname including locale prefix for accurate matching
    const fullPath = pathname || '/';
    
    // Check legal pages first (using full path to match correctly)
    const isPrivacyPage = fullPath === '/privacy-policy' || fullPath === '/nl/privacy-verklaring' || fullPath === '/de/datenschutz';
    if (isPrivacyPage) return ROUTES.privacy[targetLocale];

    const isTermsPage = fullPath === '/terms-and-conditions' || fullPath === '/nl/algemene-voorwaarden' || fullPath === '/de/allgemeine-geschaeftsbedingungen';
    if (isTermsPage) return ROUTES.terms[targetLocale];

    const isCookiesPage = fullPath === '/cookie-policy' || fullPath === '/nl/cookiebeleid' || fullPath === '/de/cookie-richtlinie';
    if (isCookiesPage) return ROUTES.cookies[targetLocale];

    // Check integrations pages
    const isIntegrationsPage = Object.values(ROUTES.integrations).some(path => currentPath === path);
    if (isIntegrationsPage) {
      return ROUTES.integrations[targetLocale];
    }

    // Check if it's a specific integration detail page
    if (currentPath.startsWith('/integrations/') || currentPath.startsWith('/integraties/') || currentPath.startsWith('/integrationen/')) {
      const slug = currentPath.split('/').pop() || '';
      return `${ROUTES.integrations[targetLocale]}/${slug}`;
    }

    // Check industries/branches/branchen overview pages
    const isIndustriesPage = Object.values(ROUTES.industries).some(path => currentPath === path);
    if (isIndustriesPage) {
      return ROUTES.industries[targetLocale];
    }

    // Check if it's a specific industry detail page - handle cross-language mapping
    if (currentPath.startsWith('/industries/') || currentPath.startsWith('/branches/') || currentPath.startsWith('/branchen/')) {
      // Extract the slug from current path
      const slug = currentPath.split('/').pop() || '';
      
      // Industry/branch slug mapping between languages
      const industryMap: Record<string, { en: string; nl: string; de: string }> = {
        // Map all possible slugs across all languages to their correct equivalents
        'restaurants': { en: 'restaurants', nl: 'restaurants', de: 'restaurants' },
        'hotels': { en: 'hotels', nl: 'hotels', de: 'hotels' },
        'cafes-bars': { en: 'cafes-bars', nl: 'cafes-bars', de: 'cafes-bars' },
        'beach-clubs': { en: 'beach-clubs', nl: 'strandtenten', de: 'strandclubs' },
        'strandtenten': { en: 'beach-clubs', nl: 'strandtenten', de: 'strandclubs' },
        'strandclubs': { en: 'beach-clubs', nl: 'strandtenten', de: 'strandclubs' },
        'events-festivals': { en: 'events-festivals', nl: 'events-festivals', de: 'events-festivals' },
        'bowling-centers': { en: 'bowling-centers', nl: 'bowlingcentra', de: 'bowlingcenter' },
        'bowlingcentra': { en: 'bowling-centers', nl: 'bowlingcentra', de: 'bowlingcenter' },
        'bowlingcenter': { en: 'bowling-centers', nl: 'bowlingcentra', de: 'bowlingcenter' },
        'zoos': { en: 'zoos', nl: 'dierentuinen', de: 'zoos' },
        'dierentuinen': { en: 'zoos', nl: 'dierentuinen', de: 'zoos' },
        'golf-courses': { en: 'golf-courses', nl: 'golfbanen', de: 'golfbahnen' },
        'golfbanen': { en: 'golf-courses', nl: 'golfbanen', de: 'golfbahnen' },
        'golfbahnen': { en: 'golf-courses', nl: 'golfbanen', de: 'golfbahnen' },
        'all-you-can-eat': { en: 'all-you-can-eat', nl: 'all-you-can-eat', de: 'all-you-can-eat' },
        'airports': { en: 'airports', nl: 'luchthavens', de: 'flughaefen' },
        'luchthavens': { en: 'airports', nl: 'luchthavens', de: 'flughaefen' },
        'flughaefen': { en: 'airports', nl: 'luchthavens', de: 'flughaefen' },
        'amusement-parks': { en: 'amusement-parks', nl: 'pretparken', de: 'freizeitparks' },
        'pretparken': { en: 'amusement-parks', nl: 'pretparken', de: 'freizeitparks' },
        'freizeitparks': { en: 'amusement-parks', nl: 'pretparken', de: 'freizeitparks' },
        'bakeries': { en: 'bakeries', nl: 'bakkerijen', de: 'baeckereien' },
        'bakkerijen': { en: 'bakeries', nl: 'bakkerijen', de: 'baeckereien' },
        'baeckereien': { en: 'bakeries', nl: 'bakkerijen', de: 'baeckereien' },
        'canteens': { en: 'canteens', nl: 'kantines', de: 'kantinen' },
        'kantines': { en: 'canteens', nl: 'kantines', de: 'kantinen' },
        'kantinen': { en: 'canteens', nl: 'kantines', de: 'kantinen' },
        'casinos': { en: 'casinos', nl: 'casinos', de: 'kasinos' },
        'kasinos': { en: 'casinos', nl: 'casinos', de: 'kasinos' },
        'catering': { en: 'catering', nl: 'catering', de: 'catering' },
        'concert-halls': { en: 'concert-halls', nl: 'concertzalen', de: 'konzertsaele' },
        'concertzalen': { en: 'concert-halls', nl: 'concertzalen', de: 'konzertsaele' },
        'konzertsaele': { en: 'concert-halls', nl: 'concertzalen', de: 'konzertsaele' },
        'congress-centers': { en: 'congress-centers', nl: 'congressen', de: 'kongresse' },
        'congressen': { en: 'congress-centers', nl: 'congressen', de: 'kongresse' },
        'kongresse': { en: 'congress-centers', nl: 'congressen', de: 'kongresse' },
        'cruise-ships': { en: 'cruise-ships', nl: 'cruiseschepen', de: 'kreuzfahrtschiffe' },
        'cruiseschepen': { en: 'cruise-ships', nl: 'cruiseschepen', de: 'kreuzfahrtschiffe' },
        'kreuzfahrtschiffe': { en: 'cruise-ships', nl: 'cruiseschepen', de: 'kreuzfahrtschiffe' },
        'expos': { en: 'expos', nl: 'beurzen', de: 'messen' },
        'beurzen': { en: 'expos', nl: 'beurzen', de: 'messen' },
        'messen': { en: 'expos', nl: 'beurzen', de: 'messen' },
        'fast-food': { en: 'fast-food', nl: 'fastfood', de: 'fastfood' },
        'fastfood': { en: 'fast-food', nl: 'fastfood', de: 'fastfood' },
        'ferries': { en: 'ferries', nl: 'ferries', de: 'faehren' },
        'faehren': { en: 'ferries', nl: 'ferries', de: 'faehren' },
        'food-halls': { en: 'food-halls', nl: 'foodhalls', de: 'food-courts' },
        'foodhalls': { en: 'food-halls', nl: 'foodhalls', de: 'food-courts' },
        'food-courts': { en: 'food-halls', nl: 'foodhalls', de: 'food-courts' },
        'food-trucks': { en: 'food-trucks', nl: 'foodtrucks', de: 'foodtrucks' },
        'foodtrucks': { en: 'food-trucks', nl: 'foodtrucks', de: 'foodtrucks' },
        'holiday-parks': { en: 'holiday-parks', nl: 'vakantieparken', de: 'ferienparks' },
        'vakantieparken': { en: 'holiday-parks', nl: 'vakantieparken', de: 'ferienparks' },
        'ferienparks': { en: 'holiday-parks', nl: 'vakantieparken', de: 'ferienparks' },
        'hospitals': { en: 'hospitals', nl: 'ziekenhuizen', de: 'krankenhaeuser' },
        'ziekenhuizen': { en: 'hospitals', nl: 'ziekenhuizen', de: 'krankenhaeuser' },
        'krankenhaeuser': { en: 'hospitals', nl: 'ziekenhuizen', de: 'krankenhaeuser' },
        'ice-cream-parlors': { en: 'ice-cream-parlors', nl: 'ijssalons', de: 'eisdielen' },
        'ijssalons': { en: 'ice-cream-parlors', nl: 'ijssalons', de: 'eisdielen' },
        'eisdielen': { en: 'ice-cream-parlors', nl: 'ijssalons', de: 'eisdielen' },
        'lunchrooms': { en: 'lunchrooms', nl: 'lunchrooms', de: 'lunchrooms' },
        'museums': { en: 'museums', nl: 'musea', de: 'museen' },
        'musea': { en: 'museums', nl: 'musea', de: 'museen' },
        'museen': { en: 'museums', nl: 'musea', de: 'museen' },
        'spa-wellness': { en: 'spa-wellness', nl: 'spa-wellness', de: 'spa-wellness' },
        'stadiums': { en: 'stadiums', nl: 'stadions', de: 'stadien' },
        'stadions': { en: 'stadiums', nl: 'stadions', de: 'stadien' },
        'stadien': { en: 'stadiums', nl: 'stadions', de: 'stadien' },
        'universities': { en: 'universities', nl: 'universiteiten', de: 'universitaeten' },
        'universiteiten': { en: 'universities', nl: 'universiteiten', de: 'universitaeten' },
        'universitaeten': { en: 'universities', nl: 'universiteiten', de: 'universitaeten' },
      };
      
      // Get the correct slug for target locale
      const mapping = industryMap[slug];
      if (mapping) {
        const targetSlug = (mapping as any)[targetLocale] || mapping.en;
        // Use correct base path for target locale (industries/branches/branchen)
        return `${ROUTES.industries[targetLocale]}/${targetSlug}`;
      }
      // Fallback for new locales: keep slug identical
      return `${ROUTES.industries[targetLocale]}/${slug}`;
    }

    // Check all solution pages
    const isKioskPage = Object.values(ROUTES.kiosk).some(path => currentPath === path);
    if (isKioskPage) return ROUTES.kiosk[targetLocale];

    const isQRPage = Object.values(ROUTES.qr).some(path => currentPath === path);
    if (isQRPage) return ROUTES.qr[targetLocale];

    const isTabletPage = Object.values(ROUTES.tablet).some(path => currentPath === path);
    if (isTabletPage) return ROUTES.tablet[targetLocale];

    const isWebshopPage = Object.values(ROUTES.webshop).some(path => currentPath === path);
    if (isWebshopPage) return ROUTES.webshop[targetLocale];

    // Check core pages
    const isDemoPage = Object.values(ROUTES.demo).some(path => currentPath === path);
    if (isDemoPage) return ROUTES.demo[targetLocale];

    const isThankYouPage = Object.values(ROUTES.thankYou).some(path => currentPath === path);
    if (isThankYouPage) return ROUTES.thankYou[targetLocale];

    // For other pages, return the same path
    return currentPath;
  };

  return (
    <div 
      className={styles.container} 
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className={styles.trigger}
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <span className={styles.flagImage}>
          <span className={styles.flag} aria-hidden="true">{emojiFlag(currentLocale)}</span>
        </span>
        <span className={styles.currentLocale}>
          {currentLocale.toUpperCase()}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className={`${styles.icon} ${isOpen ? styles.iconOpen : ''}`}
        >
          <path
            d="M2 4L6 8L10 4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdown} role="menu">
          {locales.map((locale) => {
            const localePath = getLocaleSpecificPath(locale, pathWithoutLocale);
            // English (default) goes to root, other languages get prefix
            const href = locale === 'en' 
              ? (localePath === '/' ? '/' : localePath)
              : `/${locale}${localePath === '/' ? '' : localePath}`;
            const isCurrent = locale === currentLocale;

            const handleLocaleChange = (e: React.MouseEvent) => {
              e.preventDefault();
              setIsOpen(false);
              
              // Set cookie and force a hard navigation to ensure middleware reads it
              document.cookie = `preferred_locale=${locale}; path=/; max-age=31536000; SameSite=Lax`;
              
              // Use window.location for hard navigation (ensures cookie is sent)
              // Small delay to ensure cookie is set
              setTimeout(() => {
                window.location.href = href;
              }, 50);
            };

            return (
              <button
                key={locale}
                type="button"
                className={`${styles.option} ${isCurrent ? styles.active : ''}`}
                role="menuitem"
                aria-current={isCurrent ? 'true' : undefined}
                onClick={handleLocaleChange}
              >
                <span className={styles.flagImage}>
                  <span className={styles.flag} aria-hidden="true">{emojiFlag(locale)}</span>
                </span>
                <span className={styles.localeName}>{localeNames[locale]}</span>
                <span className={styles.localeCode}>{locale.toUpperCase()}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
